import React from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle, Package, ArrowLeft } from 'lucide-react'

const ShopifyDemoPage: React.FC = () => {
  const navigate = useNavigate()
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } }
  }

  const cardVariants = {
    hidden: { y: 25, opacity: 0, scale: 0.98 },
    visible: { y: 0, opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } }
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="pt-10 pb-16">
          <Link to="/" className="inline-flex items-center space-x-2 text-[#6E6D7A] hover:text-[#1A2B49] transition-colors duration-300 mb-10">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <div className="text-center">
            <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-[#1A2B49]">Shopify Integration Demos</motion.h1>
            <motion.p variants={itemVariants} className="text-lg md:text-xl max-w-3xl mx-auto text-[#6E6D7A] leading-relaxed">Explore how MomentScience seamlessly integrates with your Shopify checkout flow to deliver personalized offers at key customer moments.</motion.p>
          </div>
        </motion.div>
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div variants={cardVariants} onClick={() => navigate('/shopify-checkout')} className="group cursor-pointer bg-white rounded-xl transition-all duration-300 hover:shadow-xl overflow-hidden flex flex-col border border-gray-200 hover:border-[#FE4215]/30">
              <div className="h-2 bg-gradient-to-r from-[#FE4215] to-[#FF6B47]"></div>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-start mb-6">
                  <div className="p-3 rounded-xl bg-[#FE4215]/10 mr-4 flex-shrink-0 group-hover:bg-[#FE4215] transition-colors duration-300"><CheckCircle className="w-7 h-7 text-[#FE4215] group-hover:text-white transition-colors duration-300" /></div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#1A2B49]">Thank You Page</h3>
                  </div>
                </div>
                <p className="text-base text-[#6E6D7A] leading-relaxed mb-6 flex-grow">Convert more customers with personalized offers on your order confirmation page. Features multiple placement options and live MomentScience offers that adapt to customer behavior.</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>6 layout placement options</li>
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>Real-time offer switching</li>
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>Live API integration</li>
                </ul>
                <button className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-[#FE4215] hover:bg-[#E63A0F] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">Start Demo →</button>
              </div>
            </motion.div>
            <motion.div variants={cardVariants} onClick={() => navigate('/shopify-order-status')} className="group cursor-pointer bg-white rounded-xl transition-all duration-300 hover:shadow-xl overflow-hidden flex flex-col border border-gray-200 hover:border-[#FE4215]/30">
              <div className="h-2 bg-gradient-to-r from-[#FE4215] to-[#FF6B47]"></div>
              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-start mb-6">
                  <div className="p-3 rounded-xl bg-[#FE4215]/10 mr-4 flex-shrink-0 group-hover:bg-[#FE4215] transition-colors duration-300"><Package className="w-7 h-7 text-[#FE4215] group-hover:text-white transition-colors duration-300" /></div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#1A2B49]">Order Status Page</h3>
                  </div>
                </div>
                <p className="text-base text-[#6E6D7A] leading-relaxed mb-6 flex-grow">Keep customers engaged while they track their order. Display contextual offers at strategic points throughout the order status experience with flexible placement controls.</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>6 strategic placements</li>
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>Order tracking UI</li>
                  <li className="flex items-center text-sm text-[#6E6D7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#FE4215] mr-2"></span>Contextual offers</li>
                </ul>
                <button className="w-full py-4 px-6 rounded-lg font-semibold text-white bg-[#FE4215] hover:bg-[#E63A0F] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">View Demo →</button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ShopifyDemoPage
